import type { OrgDto, SitePlace } from '@nsb/core';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { PlaceInput } from '../../components/PlaceInput.tsx';
import { Button, ErrorLine, Field, LoadError, Loading, NumberInput, Page, TextInput, TextLink, Title } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { orgChanges, orgFields } from '../formChanges.ts';

/**
 * /settings opens on the organization, the query string kept. An OAuth
 * sign-in that comes back with its answer (?connected= or ?error=), from an
 * old link or an older deploy, goes on to Connections, where that answer is
 * shown.
 */
export function SettingsHome() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const to = params.has('connected') || params.has('error') ? '/settings/connections' : '/settings/organization';
  return <Navigate to={`${to}${search}`} replace />;
}

/**
 * Settings → Organization (spec §11): its name, where it is, and the fee and contingency every project inherits. The
 * banners painted from where it is, which a project shows until it has its own, are under Art (L-14, Jason, 2026-10-07:
 * "this banner stuff should be in art").
 */
export function OrganizationSettings() {
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const { data: org, error } = useApi<OrgDto>(['org'], '/org');
  useFeedbackRecord(null, null);
  if (error) return <LoadError error={error} />;
  if (!org) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  return (
    <Page>
      <Title kicker="Settings">{org.name}</Title>
      <OrgForm key={JSON.stringify(org)} org={org} admin={admin} />
    </Page>
  );
}

/** The organization's facts. A save is announced by the shared notice (api.ts), so the form says nothing of its own. */
export function OrgForm({ org, admin }: { org: OrgDto; admin: boolean }) {
  const [name, setName] = useState(org.name);
  const [fee, setFee] = useState<number | null>(org.feePct);
  const [cont, setCont] = useState<number | null>(org.contingencyPct);
  const [where, setWhere] = useState<{ text: string; place: SitePlace | null }>({ text: org.placeAddress ?? '', place: org.place });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/org', { method: 'PATCH', body: { name, feePct: fee, contingencyPct: cont, placeAddress: where.text.trim() || null, place: where.place } });
      await queryClient.invalidateQueries({ queryKey: ['org'] });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  // Changes not saved, on the bar at the window's foot, which saves as Save below does (P-29); an admin's, as the form is.
  useUnsaved({
    names: admin ? orgChanges(org, { name, fee, cont, where }) : [],
    busy,
    canSave: fee !== null && cont !== null,
    error: err,
    onSave: save,
    onDiscard: () => {
      const s = orgFields(org);
      setName(s.name);
      setFee(s.fee);
      setCont(s.cont);
      setWhere(s.where);
      setErr(null);
    },
  });
  return (
    <div>
      <Field label="Organization">{(id) => <TextInput id={id} value={name} disabled={!admin} onChange={(e) => setName(e.target.value)} />}</Field>
      <Field
        label="Where NSB is"
        hint={
          <>
            Only its town and state are ever sent out. The banners painted from here are under <TextLink to="/settings/art">Art</TextLink>.
          </>
        }
      >
        {(id) =>
          admin ? (
            <PlaceInput id={id} value={where.text} place={where.place} onChange={(text, place) => setWhere({ text, place })} />
          ) : (
            <TextInput id={id} value={where.text} disabled />
          )
        }
      </Field>
      <div className="grid grid-cols-2 gap-6">
        <Field label="Fee %" hint="Project management fee on direct cost.">
          {(id) => <NumberInput id={id} value={fee} disabled={!admin} onChange={setFee} />}
        </Field>
        <Field label="Contingency %" hint="Reserve on direct cost.">
          {(id) => <NumberInput id={id} value={cont} disabled={!admin} onChange={setCont} />}
        </Field>
      </div>
      <p className="meta -mt-2 mb-4">
        Projects inherit these unless their own settings change them. The contingency is held inside a client’s budget or on top of it: a new
        one is proposed to each client with a budget, at the same amount with the contingency where it was, split anew, for them to confirm. The
        fee waits for the estimate, built again on the steps.
      </p>
      <ErrorLine error={err} />
      {admin ? (
        <Button onClick={() => void save()} busy={busy} disabled={fee === null || cont === null}>
          Save
        </Button>
      ) : null}
    </div>
  );
}
